import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, X, Loader2, Shield, Users, Edit3 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { rolesService, Role, Permission } from '@/services/roles.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { RoleFormModal } from './components/role-form-modal';
import { dialogs } from '@/stores/dialog-store';

export function RolesPage() {
  const { user } = useAuthStore();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { error, errorType, handleApiError: onApiError, handleAuthRedirect } = useApiError();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const [rolesData, permsData] = await Promise.all([
        rolesService.findByCompany(user.company.id),
        rolesService.getPermissions(),
      ]);
      setRoles(rolesData || []);
      setPermissions(permsData || []);
    } catch (err: any) { onApiError(err); }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = async () => {
    setEditingRole(null);
    setModalOpen(true);
  };

  const openEdit = async (r: Role) => {
    setEditingRole(r);
    setModalOpen(true);
  };

  const handleSave = async (data: { name: string; description: string; permissionIds: string[] }) => {
    if (!user?.company?.id) return;
    setSaving(true);
    try {
      if (editingRole) {
        await rolesService.update(editingRole.id, data);
      } else {
        await rolesService.create({
          companyId: user.company.id,
          ...data,
        });
      }
      setModalOpen(false);
      loadData();
    } catch (err: any) { onApiError(err); }
    setSaving(false);
  };

  const handleDelete = async (r: Role) => {
    if (r.name === 'SUPER_ADMIN') {
      await dialogs.alert('El rol SUPER_ADMIN no se puede eliminar.');
      return;
    }
    if (r._count.users > 0) {
      await dialogs.alert(`No se puede eliminar el rol porque hay ${r._count.users} usuario(s) asignado(s).`);
      return;
    }
    if (!await dialogs.confirm(`¿Eliminar rol "${r.name}" permanentemente?`)) return;
    
    try {
      await rolesService.remove(r.id);
      loadData();
    } catch (err: any) { onApiError(err); }
  };

  const filtered = roles.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      (r.description && r.description.toLowerCase().includes(q))
    );
  });

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadData} onLogin={handleAuthRedirect} />
      
      {!error && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Roles y Permisos</h1>
              <p className="text-sm text-ios-gray mt-1">{roles.length} roles configurados</p>
            </div>
            <button onClick={openCreate} className="ios-button flex items-center gap-2 px-4 py-2">
              <Plus size={16} />
              Nuevo Rol
            </button>
          </div>

          <div className="relative max-w-sm mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar rol..."
              className="ios-input w-full pl-9 h-9 text-sm"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 size={24} className="animate-spin text-ios-gray" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="ios-card p-12 text-center">
              <Shield size={48} className="mx-auto text-ios-gray-4 mb-4" />
              <h3 className="text-lg font-medium text-ios-gray mb-1">
                {search ? 'Sin resultados' : 'No hay roles'}
              </h3>
              <p className="text-sm text-ios-gray mb-4">
                {search ? 'Intenta con otros términos' : 'Crea tu primer rol personalizado'}
              </p>
            </div>
          ) : (
            <div className="ios-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                      <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Rol</th>
                      <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Descripción</th>
                      <th className="text-center px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Usuarios</th>
                      <th className="text-center px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Permisos</th>
                      <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ios-gray-5">
                    {filtered.map((r) => (
                      <tr key={r.id} className="hover:bg-ios-gray-6/30 transition-colors">
                        <td className="px-4 py-3 font-medium text-ios-blue cursor-pointer" onClick={() => openEdit(r)}>
                          {r.name}
                          {r.name === 'SUPER_ADMIN' && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-purple-100 text-purple-700">
                              SISTEMA
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-ios-gray">{r.description || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center gap-1 bg-ios-gray-6 px-2 py-1 rounded-lg text-xs">
                            <Users size={12} /> {r._count?.users || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center gap-1 bg-ios-gray-6 px-2 py-1 rounded-lg text-xs">
                            <Shield size={12} /> {r.rolePermissions?.length || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEdit(r)}
                              className="p-1.5 rounded-lg hover:bg-ios-blue/5 text-ios-gray hover:text-ios-blue transition-colors"
                              title="Editar"
                            >
                              <Edit3 size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(r)}
                              disabled={r.name === 'SUPER_ADMIN' || r._count?.users > 0}
                              className={`p-1.5 rounded-lg transition-colors ${
                                r.name === 'SUPER_ADMIN' || r._count?.users > 0
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-ios-gray hover:text-ios-red hover:bg-red-50'
                              }`}
                              title={r.name === 'SUPER_ADMIN' ? 'Rol protegido' : r._count?.users > 0 ? 'En uso' : 'Eliminar'}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {modalOpen && (
            <RoleFormModal
              role={editingRole}
              permissions={permissions}
              onClose={() => setModalOpen(false)}
              onSave={handleSave}
              saving={saving}
            />
          )}
        </>
      )}
    </div>
  );
}
